// Spatial calibration: convert normalized pose distances to real-world units
// using the patient's height (anthropometric scaling — the approach markerless
// apps like OPED use). Accuracy is ~±10–15%; every spatial value carries this
// confidence and a clear "estimated" label in the UI/report.
//
// MediaPipe normalizes x by frame width and y by frame height SEPARATELY, so
// raw normalized coordinates are anisotropic. We restore an isotropic metric by
// scaling x with the aspect ratio (width/height) before measuring distances.

import { Joints, type PoseFrame, type Side } from "../pose/landmarks";
import { percentile } from "./geometry";

/** Leg length (hip→ankle) as a fraction of standing height (anthropometric). */
const LEG_TO_HEIGHT = 0.49;

export interface Calibration {
  /** Centimetres per isotropic normalized unit (y-axis basis). */
  cmPerUnit: number;
  confidence: "high" | "medium" | "low";
  source: "height";
}

export interface IsoPoint {
  x: number;
  y: number;
}

/** Isotropic distance between two landmarks (x scaled by aspect = width/height). */
export function isoDist(
  a: { x: number; y: number },
  b: { x: number; y: number },
  aspect: number,
): number {
  return Math.hypot((a.x - b.x) * aspect, a.y - b.y);
}

/**
 * Build a height-based calibration. Uses the median visible leg length across
 * the clip as the stable scale reference.
 */
export function calibrateFromHeight(
  frames: PoseFrame[],
  aspect: number,
  heightCm: number,
): Calibration | null {
  if (!heightCm || heightCm <= 0) return null;

  const legLens: number[] = [];
  for (const f of frames) {
    const j = new Joints(f);
    for (const side of ["L", "R"] as Side[]) {
      const hip = j.hip(side);
      const ankle = j.ankle(side);
      if (Math.min(hip.visibility, ankle.visibility) < 0.5) continue;
      legLens.push(isoDist(hip, ankle, aspect));
    }
  }
  if (legLens.length < 5) return null;

  const legIso = percentile(legLens, 50);
  if (legIso <= 1e-4) return null;

  const realLegCm = LEG_TO_HEIGHT * heightCm;
  const cmPerUnit = realLegCm / legIso;

  // Confidence from how many frames contributed a clean leg measurement.
  const coverage = legLens.length / Math.max(1, frames.length * 2);
  const confidence = coverage > 0.7 ? "high" : coverage > 0.4 ? "medium" : "low";

  return { cmPerUnit, confidence, source: "height" };
}
